import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Instagram, Menu, X, MapPin } from 'lucide-react';

const LOGO_URL = "/logos/ees-horizontal.jpg";
const LOGO_FOOTER_URL = "/logos/ees-vertical-negative.jpg";

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/gallery', label: 'Gallery' },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-white border-b border-gray-200">
      {/* Top bar */}
      <div className="bg-[#3D3D3D] text-white text-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href="mailto:leigh@eastwideelectrical.com.au" data-testid="header-email" className="flex items-center gap-2 hover:text-[#7AC943] transition-colors duration-300">
              <Mail size={14} />
              <span className="hidden sm:inline">leigh@eastwideelectrical.com.au</span>
            </a>
            <a href="tel:0409015781" data-testid="header-phone" className="flex items-center gap-2 hover:text-[#7AC943] transition-colors duration-300">
              <Phone size={14} />
              <span>0409 015 781</span>
            </a>
          </div>
          <a href="https://www.instagram.com/eastwide_electricalservices/" target="_blank" rel="noopener noreferrer" data-testid="header-instagram" className="hover:text-[#7AC943] transition-colors duration-300">
            <Instagram size={16} />
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link">
          <img src={LOGO_URL} alt="Eastwide Electrical Services" className="h-14 md:h-20 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map(link => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 ${
                location.pathname === link.to ? 'text-[#7AC943]' : 'text-[#3D3D3D] hover:text-[#7AC943]'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href="tel:0409015781"
            data-testid="nav-cta-button"
            className="bg-[#7AC943] text-black text-sm font-semibold uppercase tracking-[0.1em] px-6 py-2.5 rounded-sm hover:bg-[#68B038] transition-colors duration-300"
          >
            Call Now
          </a>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-[#3D3D3D]"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 px-6 py-4" data-testid="mobile-nav">
          {NAV_LINKS.map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`block py-3 text-sm uppercase tracking-[0.15em] font-medium ${
                location.pathname === link.to ? 'text-[#7AC943]' : 'text-[#3D3D3D]'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href="tel:0409015781"
            className="block mt-3 bg-[#7AC943] text-black text-sm font-semibold uppercase tracking-[0.1em] px-6 py-2.5 rounded-sm text-center"
          >
            Call Now
          </a>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#3D3D3D] text-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <img
              src={LOGO_FOOTER_URL}
              alt="Eastwide Electrical Services"
              className="h-28 w-auto mb-6"
            />
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              Registered Electrical Contractor servicing Victoria. Quality workmanship, reliable service.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] font-semibold mb-6 text-[#7AC943]">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              {NAV_LINKS.map(link => (
                <Link key={link.to} to={link.to} className="text-gray-400 hover:text-white transition-colors duration-300 text-sm">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] font-semibold mb-6 text-[#7AC943]">Get In Touch</h4>
            <div className="flex flex-col gap-4">
              <a
                href="https://www.google.com/maps/search/?api=1&query=6+Gabrielle+Ct+Bayswater+North+VIC+3153"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address"
                className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300 text-sm"
              >
                <MapPin size={16} className="text-[#7AC943] flex-shrink-0 mt-0.5" />
                <span>6 Gabrielle Ct, Bayswater North VIC 3153</span>
              </a>
              <a href="mailto:leigh@eastwideelectrical.com.au" data-testid="footer-email" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300 text-sm">
                <Mail size={16} className="text-[#7AC943] flex-shrink-0" />
                leigh@eastwideelectrical.com.au
              </a>
              <a href="tel:0409015781" data-testid="footer-phone" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300 text-sm">
                <Phone size={16} className="text-[#7AC943] flex-shrink-0" />
                0409 015 781
              </a>
              <a href="https://www.instagram.com/eastwide_electricalservices/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300 text-sm">
                <Instagram size={16} className="text-[#7AC943] flex-shrink-0" />
                @eastwide_electricalservices
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-gray-600 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-xs">
            &copy; {new Date().getFullYear()} Eastwide Electrical Services. All rights reserved.
          </p>
          <p className="text-gray-500 text-xs">
            Registered Electrical Contractor &middot; REC 29030 &middot; ABN 50 322 915 594
          </p>
        </div>
      </div>
    </footer>
  );
}
